<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import type { Artifact } from '../game/inventory-api';
import { itemArt, SHOP_ART } from '../game/art';
import { RARITY_HEX, SLOT_LABELS, scaledStats, STAT_META } from '../game/item-math';
import type { EquipSlot } from '../game/inventory-api';

/**
 * CHEST REVEAL — the Golden Chest opening cinematic. A closed chest rattles,
 * bursts open in a spray of rarity-colored light, and the won piece rises out
 * on a glowing card. Reduced-motion jumps straight to the reveal. Purely
 * presentational — the item is already granted server-side by the time this
 * shows; closing just dismisses.
 */
const props = defineProps<{ reward: Artifact }>();
const emit = defineEmits<{ (e: 'close'): void }>();

// phases: 'shut' (rattle) → 'burst' (open + rays + flash) → 'reveal' (card up)
const phase = ref<'shut' | 'burst' | 'reveal'>('shut');
const timers: number[] = [];

const reduce = typeof window !== 'undefined'
  && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const accent = computed(() => RARITY_HEX[props.reward.rarity]);
const rarityLabel = computed(() =>
  props.reward.rarity.charAt(0) + props.reward.rarity.slice(1).toLowerCase());
const slotLabel = computed(() => SLOT_LABELS[props.reward.type.toLowerCase() as EquipSlot]);
const stats = computed(() => scaledStats(props.reward));
const chestArt = SHOP_ART.chest_pull;

function onKey(e: KeyboardEvent) {
  if (e.key === 'Escape') emit('close');
}

onMounted(() => {
  window.addEventListener('keydown', onKey);
  if (reduce) { phase.value = 'reveal'; return; }
  timers.push(window.setTimeout(() => { phase.value = 'burst'; }, 950));
  timers.push(window.setTimeout(() => { phase.value = 'reveal'; }, 1450));
});
onUnmounted(() => {
  window.removeEventListener('keydown', onKey);
  timers.forEach((t) => window.clearTimeout(t));
});
</script>

<template>
  <div
    class="cr-scrim"
    :style="{ '--rar': accent }"
    @click.self="phase === 'reveal' && emit('close')"
  >
    <!-- radial rays + flash, tinted to the item's rarity -->
    <div v-if="phase !== 'shut'" class="cr-rays" aria-hidden="true"></div>
    <div v-if="phase === 'burst'" class="cr-flash" aria-hidden="true"></div>

    <!-- the chest itself — visible until it bursts -->
    <div v-if="phase !== 'reveal'" class="cr-chest" :class="phase === 'burst' ? 'is-burst' : 'is-shut'">
      <img v-if="chestArt" :src="chestArt" alt="" />
    </div>

    <!-- the prize -->
    <div v-if="phase === 'reveal'" class="cr-card" role="dialog" aria-label="Chest reward">
      <div class="cr-kicker">The chest gives up</div>
      <div class="cr-art-wrap">
        <span class="cr-halo" aria-hidden="true"></span>
        <img v-if="itemArt(reward.type, reward.rarity, reward.name)" :src="itemArt(reward.type, reward.rarity, reward.name)" :alt="reward.name" class="cr-art" />
      </div>
      <div class="cr-rarity">{{ rarityLabel }} · {{ slotLabel }}<span v-if="reward.level"> · Lv {{ reward.level }}</span></div>
      <h2 class="cr-name">{{ reward.name }}</h2>

      <div class="cr-stats">
        <template v-for="(v, k) in stats" :key="k">
          <span v-if="v !== 0" class="cr-stat">
            <b>{{ v }}</b>
            <i>{{ k === 'hp' ? 'HP' : STAT_META[k as 'attack'].short }}</i>
          </span>
        </template>
      </div>

      <p class="cr-note">Stashed in your bag.</p>
      <button class="btn-deco btn-deco--gold btn-deco--lg cr-take" @click="emit('close')">Take it</button>
    </div>
  </div>
</template>

<style scoped>
.cr-scrim {
  position: fixed; inset: 0; z-index: 500;
  display: grid; place-items: center;
  background: radial-gradient(120% 90% at 50% 45%, rgba(10, 7, 18, 0.86), rgba(4, 3, 8, 0.96));
  backdrop-filter: blur(4px);
  overflow: hidden;
}

/* ── rays: a conic fan of rarity light spinning slowly behind the action ── */
.cr-rays {
  position: absolute; width: 150vmax; height: 150vmax;
  background: repeating-conic-gradient(
    from 0deg,
    color-mix(in srgb, var(--rar) 42%, transparent) 0deg 6deg,
    transparent 6deg 14deg
  );
  opacity: 0.5;
  mask-image: radial-gradient(circle, #000 0%, #000 22%, transparent 62%);
  -webkit-mask-image: radial-gradient(circle, #000 0%, #000 22%, transparent 62%);
  animation: cr-spin 22s linear infinite, cr-rays-in 500ms ease both;
}
@keyframes cr-spin { to { transform: rotate(360deg); } }
@keyframes cr-rays-in { from { opacity: 0; transform: scale(0.4); } to { opacity: 0.5; } }

.cr-flash {
  position: absolute; inset: 0;
  background: radial-gradient(circle at 50% 48%, #fff, color-mix(in srgb, var(--rar) 60%, #fff) 30%, transparent 62%);
  animation: cr-flash 500ms ease-out both;
}
@keyframes cr-flash {
  0% { opacity: 0; }
  22% { opacity: 0.92; }
  100% { opacity: 0; }
}

/* ── chest ── */
.cr-chest {
  position: relative; z-index: 2;
  width: 168px; height: 168px;
  filter: drop-shadow(0 10px 30px rgba(0, 0, 0, 0.7)) drop-shadow(0 0 18px color-mix(in srgb, var(--rar) 45%, transparent));
}
.cr-chest img { width: 100%; height: 100%; object-fit: contain; }
.cr-chest.is-shut { animation: cr-rattle 950ms ease-in-out both; }
.cr-chest.is-burst { animation: cr-burst 500ms cubic-bezier(0.3, 0, 0.2, 1) both; }
@keyframes cr-rattle {
  0%, 100% { transform: translateY(0) rotate(0); }
  10% { transform: translateX(-5px) rotate(-4deg); }
  20% { transform: translateX(5px) rotate(4deg); }
  32% { transform: translateX(-6px) rotate(-5deg); }
  44% { transform: translateX(6px) rotate(5deg); }
  60% { transform: translateX(-7px) rotate(-6deg) scale(1.03); }
  74% { transform: translateX(7px) rotate(6deg) scale(1.05); }
  88% { transform: translateY(-6px) scale(1.08); }
}
@keyframes cr-burst {
  0% { transform: scale(1.08); opacity: 1; }
  40% { transform: scale(1.35) translateY(-8px); opacity: 1; }
  100% { transform: scale(2) translateY(-30px); opacity: 0; }
}

/* ── prize card ── */
.cr-card {
  position: relative; z-index: 3;
  width: min(360px, 90vw);
  padding: 22px 24px 20px;
  text-align: center;
  border-radius: 14px;
  border: 1px solid color-mix(in srgb, var(--rar) 60%, transparent);
  background:
    radial-gradient(120% 80% at 50% 0%, color-mix(in srgb, var(--rar) 16%, transparent), transparent 60%),
    linear-gradient(170deg, #16121f, #0a0712);
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.8), 0 0 40px color-mix(in srgb, var(--rar) 28%, transparent);
  animation: cr-card-in 460ms cubic-bezier(0.2, 0.8, 0.25, 1) both;
}
@keyframes cr-card-in {
  from { opacity: 0; transform: translateY(26px) scale(0.9); }
  to { opacity: 1; transform: none; }
}
.cr-kicker {
  font-family: var(--font-label, sans-serif);
  font-size: 9px; letter-spacing: 0.3em; text-transform: uppercase;
  color: var(--color-text-dim);
}
.cr-art-wrap {
  position: relative; width: 108px; height: 108px; margin: 12px auto 8px;
  display: grid; place-items: center;
}
.cr-halo {
  position: absolute; inset: -18%;
  border-radius: 50%;
  background: radial-gradient(circle, color-mix(in srgb, var(--rar) 55%, transparent), transparent 68%);
  animation: cr-halo 2.6s ease-in-out infinite;
}
@keyframes cr-halo {
  0%, 100% { opacity: 0.55; transform: scale(0.94); }
  50% { opacity: 0.95; transform: scale(1.06); }
}
.cr-art {
  position: relative; width: 100%; height: 100%; object-fit: contain;
  filter: drop-shadow(0 6px 16px rgba(0, 0, 0, 0.6));
}
.cr-rarity {
  font-family: var(--font-label, sans-serif);
  font-size: 10px; letter-spacing: 0.16em; text-transform: uppercase;
  color: var(--rar);
}
.cr-name {
  font-family: var(--font-heading, serif);
  font-size: 22px; font-weight: 700; line-height: 1.15;
  color: #f0e6d0; margin-top: 2px;
}
.cr-stats {
  display: flex; flex-wrap: wrap; justify-content: center; gap: 8px;
  margin-top: 14px;
}
.cr-stat {
  display: inline-flex; align-items: baseline; gap: 5px;
  padding: 5px 10px;
  border: 1px solid color-mix(in srgb, var(--rar) 30%, var(--color-border));
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.25);
}
.cr-stat b {
  font-family: var(--font-heading, serif); font-size: 15px; color: #f0e6d0;
  font-variant-numeric: tabular-nums;
}
.cr-stat i {
  font-family: var(--font-label, sans-serif); font-style: normal;
  font-size: 9px; letter-spacing: 0.1em; text-transform: uppercase; color: var(--color-text-dim);
}
.cr-note {
  font-family: var(--font-body, serif); font-style: italic;
  font-size: 12px; color: var(--color-text-dim); margin-top: 12px;
}
.cr-take { margin-top: 14px; }

@media (prefers-reduced-motion: reduce) {
  .cr-rays { animation: none; opacity: 0.35; }
  .cr-card { animation: none; }
  .cr-halo { animation: none; }
}
</style>
